import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "campaign-builder/ui";
import { CardBrandIcon } from "./CardBrandIcon";
import { useCountryAddressConfig } from "./useCountryAddressConfig";
import { useAddressAutocomplete } from "./useAddressAutocomplete";
import countryList from "./data/countryList.json";
import type { UseCardInputReturn } from "./useCardInput";
import { useTheme } from "campaign-builder/theme";

// Stripe Elements render inside an iframe, so they can't inherit our Tailwind
// `text-*` classes — the text/placeholder colors must be passed as concrete
// values. Resolve them from the live `--sds-*` tokens (which the `.dark` class
// re-points) so the card fields match the rest of the drawer in both light and
// dark. Without this the fields kept the light `#222336` text and turned
// near-invisible on the dark field background (PAD-31689).
//
// The tokens are stored as CSS Color 4 space-separated HSL triples ("210 40% 98%").
// Stripe's style validator rejects `hsl(210 40% 98%)` ("Invalid style
// configuration value … contains invalid characters"), and that error also breaks
// card-field input. So resolve each token to the browser-computed `rgb()` value,
// which Stripe always accepts.
function readTokenColor(varName: string, fallback: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (!raw) return fallback;
  try {
    const probe = document.createElement("span");
    probe.style.color = `hsl(${raw})`;
    probe.style.position = "absolute";
    probe.style.opacity = "0";
    probe.style.pointerEvents = "none";
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe).color;
    document.body.removeChild(probe);
    // getComputedStyle returns "rgb(...)" / "rgba(...)"; fall back if the browser
    // couldn't parse the token for any reason.
    return rgb && rgb.startsWith("rgb") ? rgb : fallback;
  } catch {
    return fallback;
  }
}

function buildStripeElementStyle() {
  return {
    base: {
      fontSize: "16px",
      fontFamily: "inherit",
      color: readTokenColor("--sds-foreground", "#222336"),
      "::placeholder": {
        color: readTokenColor("--sds-muted-foreground", "#626379"),
      },
    },
    invalid: {
      color: readTokenColor("--sds-destructive", "#d32f2f"),
    },
  };
}

// Design-system tokens matching the sibling OrderDetailsDrawer (shadcn/Tailwind).
const FIELD = "flex flex-col gap-1";
const LABEL = "text-muted-foreground text-sm font-normal leading-[18px]";
const INPUT =
  "h-10 rounded-lg border border-solid border-border bg-background px-2 py-1 text-base font-normal text-foreground outline-none placeholder:text-muted-foreground focus:border-ring";
const FIELD_ERROR = "text-destructive text-xs leading-4";
const STRIPE_EL = "h-10 rounded-lg border border-solid border-border px-2 py-2.5 transition-colors";
const SAVE_CARD_HELPER = "Your card will be securely stored for faster checkout.";

type BillingAddressMode = "default" | "custom";

export interface AddCardFormData {
  billingMode: BillingAddressMode;
  billingAddress: {
    countryCode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    region: string;
    postalCode: string;
  } | null;
}

interface NewCardFormProps {
  cardInput: UseCardInputReturn;
  saveCard: boolean;
  onSaveCardChange: (checked: boolean) => void;
  /** Why the entered card can't be saved (prepaid or unknown type, PAD-32618); locks the checkbox and replaces the helper line. */
  saveCardBlockedReason: string | null;
  /** Stripe is reading the card's funding; Confirm waits so a card that can't be saved never hits the save path. */
  isCheckingFunding: boolean;
  hasDefaultAddress: boolean;
  onConfirm: (formData: AddCardFormData) => void;
  /**
   * Preselects the billing country so the address fields render with the right
   * region/postal labels immediately. Comes from the campaign's client country
   * — a stored account fact, which is steadier than geo-IP (VPNs, travel) and
   * already the country whose Stripe account will take the payment. The user can
   * still change it.
   */
  defaultCountryCode?: string;
}

type AutocompleteOption = { value: string; label: string };

// Searchable single-select, mirroring the Targeting CitySelect (shadcn Popover +
// Command). The z-[1500] override keeps the popover above the checkout drawer /
// sidebar (z-1400) — the same reason the old MUI popper forced z-index 1500.
const AddressCombobox = ({
  options,
  value,
  onChange,
  placeholder,
  cypressId,
}: {
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  cypressId?: string;
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value) || null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-cypress-id={cypressId}
          className="flex h-10 w-full items-center justify-between rounded-lg border border-solid border-border bg-background px-2 text-base text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[1500] w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const countryOptions: AutocompleteOption[] = (countryList as { code: string; name: string }[]).map((c) => ({
  value: c.code,
  label: c.name,
}));

export const NewCardForm: React.FC<NewCardFormProps> = ({
  cardInput,
  saveCard,
  onSaveCardChange,
  saveCardBlockedReason,
  isCheckingFunding,
  hasDefaultAddress,
  onConfirm,
  defaultCountryCode,
}) => {
  const saveCardBlocked = saveCardBlockedReason !== null;
  const { fieldErrors } = cardInput;
  // Recompute the Stripe iframe style whenever the theme flips so typed card
  // text tracks the palette (PAD-31689).
  const theme = useTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stripeElementStyle = useMemo(() => buildStripeElementStyle(), [theme]);
  // No default on file → force custom mode and hide the radio group so the
  // user can't skip entering an address.
  const [billingMode, setBillingMode] = useState<BillingAddressMode>(hasDefaultAddress ? "default" : "custom");

  // Keep billingMode in sync with hasDefaultAddress after the initial mount
  // (e.g. if onboard data resolves late, the user adds/removes their default
  // in another tab, or the form is ever rendered before loading completes).
  useEffect(() => {
    setBillingMode(hasDefaultAddress ? "default" : "custom");
  }, [hasDefaultAddress]);

  // Custom billing address fields
  const [countryCode, setCountryCode] = useState((defaultCountryCode || "").toUpperCase());
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [postalError, setPostalError] = useState("");
  const [addressOpen, setAddressOpen] = useState(false);
  const pickingRef = useRef(false);
  const {
    suggestions,
    search: searchAddress,
    resolve: resolveAddress,
    clear: clearAddress,
  } = useAddressAutocomplete(countryCode);

  const applyResolvedAddress = async (placeId: string) => {
    pickingRef.current = true;
    setAddressOpen(false);
    clearAddress();
    const resolved = await resolveAddress(placeId);
    if (resolved) {
      // Only overwrite what Google returned, so a hand-typed value survives.
      if (resolved.addressLine1) setAddressLine1(resolved.addressLine1);
      if (resolved.city) setCity(resolved.city);
      if (resolved.zipCode) setPostalCode(resolved.zipCode);
      if (resolved.country) setCountryCode(resolved.country);
      if (resolved.state) setRegion(resolved.state);
      setPostalError("");
    }
    pickingRef.current = false;
  };

  const { config, status } = useCountryAddressConfig(countryCode);

  const regionAutocompleteOptions: AutocompleteOption[] = useMemo(
    () => (config.regionOptions || []).map((r) => ({ value: r.code, label: r.name })),
    [config.regionOptions]
  );

  const handlePostalBlur = () => {
    if (config.postalRegex && postalCode) {
      try {
        const regex = new RegExp(`^${config.postalRegex}$`, "i");
        if (!regex.test(postalCode)) {
          setPostalError(`Invalid ${config.postalLabel.toLowerCase()}`);
        } else {
          setPostalError("");
        }
      } catch {
        setPostalError("");
      }
    } else {
      setPostalError("");
    }
  };

  const isCustomAddressIncomplete =
    billingMode === "custom" &&
    (status === "loading" ||
      !countryCode ||
      !addressLine1.trim() ||
      !city.trim() ||
      (config.regionRequired && !region) ||
      (config.postalRequired && !postalCode.trim()) ||
      !!postalError);

  const handleCountryChange = (code: string) => {
    setCountryCode(code);
    setRegion("");
    setPostalCode("");
    setPostalError("");
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-solid border-border bg-background p-6">
      <h3 className="text-foreground m-0 text-base font-semibold leading-6" data-test-id={`enterCardDetailsTitle`}>
        Enter your card details
      </h3>

      {/* Card Holder Name */}
      <div className={FIELD}>
        <label className={LABEL} data-test-id={`cardHolderNameInputTitle`}>
          Card Holder Name
        </label>
        <input
          type="text"
          className={INPUT}
          placeholder="Full name on card"
          value={cardInput.cardholderName}
          onChange={(e) => cardInput.setCardholderName(e.target.value)}
          data-test-id={`cardHolderNameInput`}
        />
        {fieldErrors.cardholderName && <span className={FIELD_ERROR}>{fieldErrors.cardholderName}</span>}
      </div>

      {/* Card number */}
      <div className={FIELD}>
        <label className={LABEL} data-test-id={`cardNumberTitle`}>
          Card number
        </label>
        <div className="flex items-center gap-2">
          <div
            className={`${STRIPE_EL} flex-1 ${fieldErrors.cardNumber ? "border-destructive" : ""}`}
            data-test-id={`cardNumberInput`}
          >
            <CardNumberElement
              options={{ style: stripeElementStyle, showIcon: false }}
              onChange={cardInput.handleCardNumberChange}
            />
          </div>
          <CardBrandIcon brand={cardInput.detectedBrand} width={32} height={32} />
        </div>
        {fieldErrors.cardNumber && (
          <span className={FIELD_ERROR} data-test-id={`cardNumberError`}>
            {fieldErrors.cardNumber}
          </span>
        )}
      </div>

      {/* Expiration + CVV */}
      <div className="flex gap-4 [&>*]:flex-1">
        <div className={FIELD}>
          <label className={LABEL} data-test-id={`expirationDateTitle`}>
            Expiration date
          </label>
          <div
            className={`${STRIPE_EL} ${fieldErrors.expiry ? "border-destructive" : ""}`}
            data-test-id={`expirationDateInput`}
          >
            <CardExpiryElement options={{ style: stripeElementStyle }} onChange={cardInput.handleExpiryChange} />
          </div>
          {fieldErrors.expiry && (
            <span className={FIELD_ERROR} data-test-id={`cardExpiredError`}>
              {fieldErrors.expiry}
            </span>
          )}
        </div>

        <div className={FIELD}>
          <label className={LABEL} data-test-id={`cvvTitle`}>
            CVV
          </label>
          <div className={`${STRIPE_EL} ${fieldErrors.cvc ? "border-destructive" : ""}`} data-test-id={`cvvInput`}>
            <CardCvcElement options={{ style: stripeElementStyle }} onChange={cardInput.handleCvcChange} />
          </div>
          {fieldErrors.cvc && (
            <span className={FIELD_ERROR} data-test-id={`invalidCvvError`}>
              {fieldErrors.cvc}
            </span>
          )}
        </div>
      </div>

      {/* Save card checkbox */}
      <div className="mt-1 flex items-start gap-2">
        <Checkbox
          id="save-card-checkbox"
          className="mt-0.5"
          checked={saveCard}
          disabled={saveCardBlocked}
          onCheckedChange={(v) => onSaveCardChange(v === true)}
          aria-describedby="save-card-helper"
          data-cypress-id="saveCardCheckbox"
        />
        <div className="flex flex-col gap-0.5">
          <label
            htmlFor="save-card-checkbox"
            className={cn(
              "text-foreground text-base font-medium leading-6",
              saveCardBlocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
            data-test-id={`saveCardCheckboxTitle`}
          >
            Save card for future payments
          </label>
          <span
            id="save-card-helper"
            className={cn(
              "text-sm font-normal leading-4",
              saveCardBlocked ? "text-[#973c00] dark:text-[#fef3c6]" : "text-muted-foreground"
            )}
            data-test-id={`saveCardHelperTitle`}
          >
            {saveCardBlockedReason ?? SAVE_CARD_HELPER}
          </span>
        </div>
      </div>

      {/* Billing Address */}
      <div className="mt-2 flex flex-col gap-3 pt-2">
        <h4 className="text-foreground m-0 text-sm font-medium leading-5" data-test-id={`billingAddressTitle`}>
          Billing Address
        </h4>

        {hasDefaultAddress && (
          <RadioGroup
            className="gap-3"
            value={billingMode}
            onValueChange={(v) => setBillingMode(v as BillingAddressMode)}
          >
            <label className="flex cursor-pointer items-start gap-2">
              <RadioGroupItem value="default" className="mt-0.5" data-test-id={`useDefaultAddressRadioBtn`} />
              <div>
                <span className="text-foreground text-base font-medium leading-6">
                  Use the default address for your account.
                </span>
                {billingMode === "default" && (
                  <p
                    className="text-muted-foreground mt-0.5 text-sm font-normal leading-4"
                    data-test-id={`defaultBillingAddressUsedText`}
                  >
                    Your default billing address will be used.
                  </p>
                )}
              </div>
            </label>

            <label className="flex cursor-pointer items-start gap-2">
              <RadioGroupItem value="custom" className="mt-0.5" data-test-id={`useDifferentAddressRadioBtn`} />
              <span className="text-foreground text-base font-medium leading-6">
                Use a different address for this card
              </span>
            </label>
          </RadioGroup>
        )}

        {billingMode === "custom" && (
          <div className="flex flex-col gap-3">
            {/* Country autocomplete */}
            <div className={FIELD}>
              <label className={LABEL}>Country</label>
              <AddressCombobox
                options={countryOptions}
                value={countryCode}
                onChange={handleCountryChange}
                placeholder="Select country"
                cypressId="billingCountrySelect"
              />
            </div>

            {/* Address line 1 — Google Places suggestions; still free text. */}
            <div className={`${FIELD} relative`}>
              <label className={LABEL}>Address line 1</label>
              <input
                type="text"
                className={INPUT}
                placeholder="Start typing your address…"
                autoComplete="off"
                value={addressLine1}
                onChange={(e) => {
                  setAddressLine1(e.target.value);
                  searchAddress(e.target.value);
                  setAddressOpen(true);
                }}
                onBlur={() => {
                  // Let a suggestion click land before the list unmounts.
                  setTimeout(() => {
                    if (!pickingRef.current) setAddressOpen(false);
                  }, 150);
                }}
              />
              {addressOpen && suggestions.length > 0 && (
                <ul
                  className="absolute top-full z-[1500] mt-1 max-h-56 w-full list-none overflow-auto rounded-lg border border-solid border-border bg-background p-1 shadow-lg"
                  data-cypress-id="checkoutAddressSuggestions"
                >
                  {suggestions.map((suggestion) => (
                    <li key={suggestion.placeId}>
                      <button
                        type="button"
                        className="text-foreground w-full cursor-pointer rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-accent"
                        onMouseDown={() => {
                          pickingRef.current = true;
                        }}
                        onClick={() => applyResolvedAddress(suggestion.placeId)}
                      >
                        {suggestion.description}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Address line 2 */}
            <div className={FIELD}>
              <label className={LABEL}>Address line 2 (optional)</label>
              <input
                type="text"
                className={INPUT}
                placeholder="Apartment, suite, unit, building, floor..."
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>

            {/* City */}
            <div className={FIELD}>
              <label className={LABEL}>City</label>
              <input
                type="text"
                className={INPUT}
                placeholder="Town / City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            {/* Region/State + Postal Code row */}
            <div className="flex gap-4 [&>*]:flex-1">
              {/* Region — dropdown, text, or hidden depending on country */}
              {config.regionFieldType !== "hidden" && (
                <div className={FIELD}>
                  <label className={LABEL}>{config.regionLabel}</label>
                  {config.regionFieldType === "dropdown" && regionAutocompleteOptions.length > 0 ? (
                    <AddressCombobox
                      options={regionAutocompleteOptions}
                      value={region}
                      onChange={setRegion}
                      placeholder={config.regionLabel}
                    />
                  ) : (
                    <input
                      type="text"
                      className={INPUT}
                      placeholder={config.regionLabel}
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                    />
                  )}
                </div>
              )}

              {/* Postal code */}
              <div className={FIELD}>
                <label className={LABEL}>{config.postalLabel}</label>
                <input
                  type="text"
                  className={`${INPUT} ${postalError ? "border-destructive" : ""}`}
                  placeholder={config.postalLabel}
                  value={postalCode}
                  onChange={(e) => {
                    setPostalCode(e.target.value);
                    setPostalError("");
                  }}
                  onBlur={handlePostalBlur}
                />
                {postalError && <span className={FIELD_ERROR}>{postalError}</span>}
              </div>
            </div>

            {/* No "Set as default Billing Address" here: the client-side write
                (PUT /billing/address) was retired with the Master Account billing
                profiles — the address goes to Stripe with the card (PAD-32909). */}
          </div>
        )}
      </div>

      {/* Confirm button */}
      <div className="flex justify-end">
        <Button
          type="button"
          className="box-border h-10 px-6"
          disabled={isCustomAddressIncomplete || isCheckingFunding}
          onClick={() => {
            if (isCustomAddressIncomplete || isCheckingFunding) {
              return;
            }
            onConfirm({
              billingMode,
              billingAddress:
                billingMode === "custom"
                  ? {
                      countryCode,
                      addressLine1,
                      addressLine2,
                      city,
                      region,
                      postalCode,
                    }
                  : null,
            });
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
